// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api";
import {
  listProcessedModelSchemas,
  getProcessedModelSchema
} from "../graphql/queries";
import {
  createProcessedModelSchema,
  deleteProcessedModelSchema,
  updateProcessedModelSchema,
} from "../graphql/mutations";
import {
createProcessedModelOutputParamCustom
} from "../graphql/customMutations"
import {
  reconcileProcessedModelSchedules,
  removeAllProcessedModelSchedules,
} from "../utils/processedModelScheduleSync";
import { runScheduleToScheduleConfig } from "../components/Schedule/scheduleConfig";

const client = generateClient();

// Legacy fallback: derive a single ScheduleConfig from a schema's runSchedule JSON (used when a
// caller doesn't pass an explicit scheduleConfigs list, e.g. the older NewProcessedModelSchemaModal).
function legacyConfigsFromRunSchedule(runSchedule) {
  if (!runSchedule) return [];
  const cfg = runScheduleToScheduleConfig(runSchedule);
  if (cfg.mode === "once" && !cfg.firstRunTime) return []; // no real schedule configured
  return [cfg];
}

const initialState = {
  loading: "idle",
  // Tracks the full-list fetch only — `loading` is shared with create/edit/delete/by-id, so it can't
  // tell "the list is loaded" from "some other write is in flight".
  listLoading: "idle",
  error: null,
  schemas: {},
  processedModelSchemas: [],
  // When a forecast was last uploaded into a model (UploadForecastModal); the Models page re-reads the models'
  // forecasts when it changes, so a new issue shows without a reload
  forecastsUploadedAt: 0,
};

export const postProcessedModelSchema = createAsyncThunk(
  "processedModelSchema/postProcessedModelSchema",
  // Legacy callers pass a raw schema input, so every key is optional.
  async (arg: { schema?: any; scheduleConfigs?: any; [key: string]: any }, thunkAPI) => {
    // Accept either { schema, scheduleConfigs } (new) or a raw schema input (legacy).
    const wrapped = arg && (arg.schema !== undefined || arg.scheduleConfigs !== undefined);
    const input = wrapped ? arg.schema : arg;
    const scheduleConfigs = wrapped ? arg.scheduleConfigs : undefined;
    try {
      const { data } = await client.graphql({
        query: createProcessedModelSchema,
        variables: { input },
      });
      const schema = data.createProcessedModelSchema;
      // Schedule rows:
      //  - raw/legacy caller (no wrapper)         → derive one from runSchedule here.
      //  - wrapped with scheduleConfigs           → reconcile them here (non-UI callers).
      //  - wrapped { schema } only (the wizard)   → the modal reconciles + logs into its op log.
      // Best-effort — never fail the schema create if the schedule write hiccups.
      try {
        if (!wrapped) {
          await reconcileProcessedModelSchedules(
            schema.id,
            legacyConfigsFromRunSchedule(schema.runSchedule),
            { isActive: schema.isActive, owner: schema.owner, organizationId: schema.organizationId }
          );
        } else if (scheduleConfigs !== undefined) {
          await reconcileProcessedModelSchedules(schema.id, scheduleConfigs, {
            isActive: schema.isActive,
            owner: schema.owner,
            organizationId: schema.organizationId,
          });
        }
      } catch (scheduleErr) {
        console.log("schedule reconcile (create) failed", scheduleErr?.message);
      }
      return schema;
    } catch (error) {
      console.log("post processed model schema error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchProcessedModelSchemaById = createAsyncThunk(
  "processedModelSchema/fetchProcessedModelSchemaById",
  async (id: string, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: getProcessedModelSchema,
        variables: { id },
      });
      return data.getProcessedModelSchema;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchProcessedModelSchemas = createAsyncThunk(
  "processedModelSchema/fetchAllProcessedModelSchemas",
  async (_, thunkAPI) => {
    try {
      // Pages through nextToken — the default single page capped the list at 100
      const items = [];
      let nextToken = null;
      do {
        const { data } = await client.graphql({
          query: listProcessedModelSchemas,
          variables: { limit: 100, nextToken },
        });
        items.push(...data.listProcessedModelSchemas.items);
        nextToken = data.listProcessedModelSchemas.nextToken;
      } while (nextToken);
      const schemaDict = items.reduce(
        (acc, curr) => ({ ...acc, [curr.id]: curr }),
        {}
      );
      return schemaDict;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
  {
    // Drop duplicate dispatches while one list is in flight (several mounts request this at once);
    // a settled fetch can still be re-run, so refresh-on-navigate keeps working.
    condition: (_, { getState }) => getState().processedModelSchemas?.listLoading !== "pending",
  }
);


export const deleteProcessedModelSchemaFunc = createAsyncThunk(
  "processedModelSchema/deleteProcessedModelSchema",
  // Typed arg so strictly-typed callers (e.g. GeneralDeleteModal) can pass { id }.
  async (input: { id: string }, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: deleteProcessedModelSchema,
        variables: { input },
      });
      // Tear down all paired schedule rows so the poller stops considering this schema.
      // Best-effort, but warn loudly on orphans — a lingering row keeps the poller firing the
      // executor for a deleted schema.
      const cleanup = await removeAllProcessedModelSchedules(input.id);
      if (cleanup && cleanup.failed !== 0) {
        console.warn(
          `[deleteProcessedModelSchema] ${input.id}: ${cleanup.failed === -1 ? "could not list" : cleanup.failed} schedule row(s) may be orphaned — the poller could keep firing for this deleted model.`
        );
      }
      return data.deleteProcessedModelSchema;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const editProcessedModelSchemaFunc = createAsyncThunk(
  "processedModelSchema/editProcessedModelSchema",
  async (arg, thunkAPI) => {
    // Accept either { input, scheduleConfigs } (new) or a raw update input (legacy).
    const wrapped = arg && (arg.input !== undefined || arg.scheduleConfigs !== undefined);
    const input = wrapped ? arg.input : arg;
    const scheduleConfigs = wrapped ? arg.scheduleConfigs : undefined;
    try {
      const { data } = await client.graphql({
        query: updateProcessedModelSchema,
        variables: { input },
      });
      const schema = data.updateProcessedModelSchema;
      // When the caller passes an explicit schedule list, reconcile it (the authoritative set):
      // create new, update changed, delete removed. A raw edit (no scheduleConfigs) leaves
      // schedules untouched. Best-effort — don't fail the schema update on a schedule hiccup.
      if (scheduleConfigs !== undefined) {
        try {
          await reconcileProcessedModelSchedules(schema.id, scheduleConfigs, {
            isActive: schema.isActive,
            owner: schema.owner,
            organizationId: schema.organizationId,
          });
        } catch (scheduleErr) {
          console.log("schedule reconcile (edit) failed", scheduleErr?.message);
        }
      }
      return schema;
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const postProcessedModelOutputParam = createAsyncThunk(
  "processedModelSchema/postProcessedModelOutputParam",
  async (input: any, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: createProcessedModelOutputParamCustom,
        variables: { input },
      });
      return data.createProcessedModelOutputParam;
    } catch (error) {
      console.log("post processed model output param error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Join table operations for linking ProcessedModelSchema to GeoPoints (Assets)
const createProcessedModelSchemaAssetMutation = /* GraphQL */ `
  mutation CreateProcessedModelSchemaAssets($input: CreateProcessedModelSchemaAssetsInput!) {
    createProcessedModelSchemaAssets(input: $input) {
      id
      processedModelSchemaID
      assetID
      organizationId
      createdAt
      updatedAt
    }
  }
`;

const deleteProcessedModelSchemaAssetMutation = /* GraphQL */ `
  mutation DeleteProcessedModelSchemaAssets($input: DeleteProcessedModelSchemaAssetsInput!) {
    deleteProcessedModelSchemaAssets(input: $input) {
      id
    }
  }
`;

export const createProcessedModelSchemaAsset = createAsyncThunk(
  "processedModelSchema/createAssetLink",
  async (input: any, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: createProcessedModelSchemaAssetMutation,
        variables: { input },
      });
      return data.createProcessedModelSchemaAssets;
    } catch (error) {
      console.log("create processed model schema asset link error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteProcessedModelSchemaAsset = createAsyncThunk(
  "processedModelSchema/deleteAssetLink",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: deleteProcessedModelSchemaAssetMutation,
        variables: { input },
      });
      return data.deleteProcessedModelSchemaAssets;
    } catch (error) {
      console.log("delete processed model schema asset link error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Join table operations for linking ProcessedModelSchema to AssetGroups
const createProcessedModelSchemaAssetGroupMutation = /* GraphQL */ `
  mutation CreateProcessedModelSchemaAssetGroups($input: CreateProcessedModelSchemaAssetGroupsInput!) {
    createProcessedModelSchemaAssetGroups(input: $input) {
      id
      processedModelSchemaID
      assetGroupID
      organizationId
      createdAt
      updatedAt
    }
  }
`;

const deleteProcessedModelSchemaAssetGroupMutation = /* GraphQL */ `
  mutation DeleteProcessedModelSchemaAssetGroups($input: DeleteProcessedModelSchemaAssetGroupsInput!) {
    deleteProcessedModelSchemaAssetGroups(input: $input) {
      id
    }
  }
`;

export const createProcessedModelSchemaAssetGroup = createAsyncThunk(
  "processedModelSchema/createAssetGroupLink",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: createProcessedModelSchemaAssetGroupMutation,
        variables: { input },
      });
      return data.createProcessedModelSchemaAssetGroups;
    } catch (error) {
      console.log("create processed model schema asset group link error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteProcessedModelSchemaAssetGroup = createAsyncThunk(
  "processedModelSchema/deleteAssetGroupLink",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: deleteProcessedModelSchemaAssetGroupMutation,
        variables: { input },
      });
      return data.deleteProcessedModelSchemaAssetGroups;
    } catch (error) {
      console.log("delete processed model schema asset group link error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const ProcessedModelSchemaSlice = createSlice({
  name: "processedModelSchemas",
  initialState,
  reducers: {
    addProcessedModelSchema: (state, action) => {
      state.schemas[action.payload.id] = action.payload;
    },
    clearProcessedModelSchemas: (state) => {
      state.schemas = {};
      state.processedModelSchemas = [];
    },
    noteForecastsUploaded: (state) => {
      state.forecastsUploadedAt = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProcessedModelSchemaById.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchProcessedModelSchemaById.fulfilled, (state, action) => {
        state.schemas[action.payload.id] = action.payload;
        state.loading = "success";
      })
      .addCase(fetchProcessedModelSchemaById.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(editProcessedModelSchemaFunc.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(editProcessedModelSchemaFunc.fulfilled, (state, action) => {
        state.schemas[action.payload.id] = action.payload;
        state.loading = "success";
      })
      .addCase(editProcessedModelSchemaFunc.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(deleteProcessedModelSchemaFunc.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(deleteProcessedModelSchemaFunc.fulfilled, (state, action) => {
        delete state.schemas[action.payload.id];
        state.processedModelSchemas = state.processedModelSchemas.filter(schema => schema.id !== action.payload.id);
        state.loading = "success";
      })
      .addCase(deleteProcessedModelSchemaFunc.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(postProcessedModelSchema.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(postProcessedModelSchema.fulfilled, (state, action) => {
        state.loading = "success";
        state.schemas[action.payload.id] = action.payload;
        state.processedModelSchemas.push(action.payload);
      })
      .addCase(postProcessedModelSchema.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchProcessedModelSchemas.pending, (state) => {
        state.loading = "pending";
        state.listLoading = "pending";
        state.error = null;
      })
      .addCase(fetchProcessedModelSchemas.fulfilled, (state, action) => {
        state.loading = "success";
        state.listLoading = "success";
        state.schemas = action.payload;
      })
      .addCase(fetchProcessedModelSchemas.rejected, (state, action) => {
        state.loading = "failed";
        state.listLoading = "failed";
        state.error = action.error.message;
      });
  },
});

// Total processed model schemas readable by the user, for the membership-tier deploy cap.
// KNOWN GAP (fails open): counts only what's currently in Redux, so a surface that hasn't run
// fetchProcessedModelSchemas — or a user past the unpaginated list's page size — can undercount and
// bypass the cap. Callers prefetch on mount to mitigate; remove once backend enforcement lands.
export const selectProcessedModelSchemaCount = (state): number =>
  Object.keys(state.processedModelSchemas?.schemas || {}).length;

// True once the full list has been fetched — callers fail open (skip the cap) until then.
export const selectProcessedModelSchemasLoaded = (state): boolean =>
  state.processedModelSchemas?.listLoading === "success";

// Whether the list fetch still needs kicking off. Separate from `loading`, which unrelated writes flip.
export const selectProcessedModelSchemasListLoading = (state): string =>
  state.processedModelSchemas?.listLoading || "idle";

export const { addProcessedModelSchema, clearProcessedModelSchemas, noteForecastsUploaded } = ProcessedModelSchemaSlice.actions;
export default ProcessedModelSchemaSlice.reducer;
